import React, { useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../services/API'
import { SoftChip, StageChip } from '../kit/Chips'
import { formatDate } from '../kit/helpers'

// Attach a tenant that has no sales lead to one that already exists, so the
// notes and timeline built up before they signed up stay with them. Used from
// the tenant page and from tenant-only rows on the pipeline.
const LinkLeadDialog = ({ open, tenant, onClose, onLinked }) => {
  const [options, setOptions] = useState(null)
  const [selected, setSelected] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setSelected(null)
    setError('')
    setOptions(null)
    API.get('/admincrm/sales/lead-options').then((res) => {
      if (res.status === 200) setOptions(res.data)
      else setError(res.data?.message || 'Could not load the leads.')
    })
  }, [open])

  const link = async () => {
    if (!selected || !tenant?._id) return
    setBusy(true)
    setError('')
    const res = await API.post('/admincrm/sales/link-tenant', {
      tenantId: tenant._id,
      leadId: selected._id,
    })
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not link the lead.')
      return
    }
    onLinked?.(res.data)
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link {tenant?.name ?? 'tenant'} to an existing lead</DialogTitle>
      <DialogContent>
        <Stack gap={2} paddingTop={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Pick the lead this organisation was being worked under. Its notes, timeline and
            follow-ups stay put; the tenant&apos;s contact details fill any gaps, and the stage
            follows the tenant (a paying tenant becomes a Customer).
          </Typography>
          <Autocomplete
            options={options ?? []}
            loading={options === null}
            value={selected}
            onChange={(event, value) => setSelected(value)}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            getOptionLabel={(option) =>
              [option.organisationName, option.contactName].filter(Boolean).join(' · ')
            }
            noOptionsText={options?.length === 0 ? 'Every lead already has a tenant' : 'No leads match'}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                <Stack gap={0.25} minWidth={0} width="100%">
                  <Stack direction="row" gap={0.75} alignItems="center" minWidth={0}>
                    <Typography fontWeight={700} noWrap>
                      {option.organisationName || 'Unnamed lead'}
                    </Typography>
                    <StageChip stage={option.stage} sx={{ height: 20, fontSize: 11 }} />
                    {option.archived && (
                      <SoftChip label="Archived" sx={{ height: 20, fontSize: 11 }} />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {[option.contactName, option.contactEmail, option.source, `added ${formatDate(option.createdAt)}`]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Existing lead" placeholder="Search by organisation or contact" autoFocus />
            )}
          />
          {selected?.archived && (
            <Alert severity="info">This lead is archived - linking it restores it to the pipeline.</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={link} disabled={!selected || busy}>
          {busy ? 'Linking…' : 'Link lead'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default LinkLeadDialog
